import _merge from 'lodash/merge';
import { useSelector } from 'react-redux';

import { API_METHODS, callReverseApi } from 'api/apiService';
import { SAVE_INQUIRIES } from 'api/CompeonReverseApi/actionTypes';
import endpoints from 'api/CompeonReverseApi/endpoints';
import { useInquiryValues } from 'components/App/InquiryValuesProvider';
import { useFormConfig } from 'config/formConfig/hooks';
import { usePartnerConfig } from 'config/partner/hooks';
import { COMPEON_PARTNER, INQUIRY_DRAFT_ID } from 'modules/Inquiry/Form/formFields';
import { InquiryType } from 'modules/Inquiry/Inquiry.type';
import { mapBFSInquiryToApi } from 'modules/Inquiry/mapInquiryToApi/mapBFSInquiryToApi';
import { mapBFSServiceInquiryToApi } from 'modules/Inquiry/mapInquiryToApi/mapBFSServiceInquiryToApi';
import { mapInquiryToApi } from 'modules/Inquiry/mapInquiryToApi/mapInquiryToApi';
import { mapProfiInquiryToApi } from 'modules/Inquiry/mapInquiryToApi/mapProfiInquiryToApi';
import { mapLeasePlanToApi } from 'modules/Inquiry/mapInquiryToApi/peopleMapper/peopleDetailsToApi';
import { addInquiryProcessPropsToPayload } from 'shared/inquiry/inquiryProcess';
import { IState } from 'store';
import {
  getInquiryDetailsSelector,
  getInquiryIdSelector,
  inquiryDetailsCustomerIdSelector,
} from 'store/inquiryDetails/selectors';
import { getInquiryDraftIdSelector, isLeadProcessLane } from 'store/inquiryProcess/selectors';
import { getCreatedInquiryId } from 'store/progress/selectors';
import { flattenObject, pipe } from 'utils/helpers';
import useDispatchApiCall, {
  dispatchOptions,
  IMakeCallResponse,
} from 'utils/hooks/useDispatchApiCallHook';
import { useTranslations } from 'utils/hooks/useTranslations';
import { isLoggedInUser, userIsCustomer } from 'utils/user/conditionals';

import { filterEmptyData, useFilterDataByBusinessConditions } from './useFilterInquiryData';
import { mapOnlinefactoringToApi } from '../../../onlinefactoring/mapOnlinefactoringToApi';

export const typeSpecificMapping: { [kind: string]: Function } = {
  [InquiryType.bfsService]: mapBFSServiceInquiryToApi,
  [InquiryType.default]: mapInquiryToApi,
  [InquiryType.leaseplan]: mapLeasePlanToApi,
  [InquiryType.bfs]: mapBFSInquiryToApi,
  [InquiryType.profiRLL]: mapProfiInquiryToApi,
};

export const createSpecializedInquiryAction = (apiData: any, inquiryType: string) => {
  return callReverseApi({
    actionName: SAVE_INQUIRIES,
    url: endpoints.INQUIRIES.SPECIALIZED.TYPE.compose({ params: { inquiryType } }),
    method: API_METHODS.POST,
    data: {
      data: {
        attributes: apiData,
      },
    },
  });
};

export const createFinalizeSpecializedInquiryAction = (
  apiData: any,
  inquiryType: string,
  inquiryId: string,
) => {
  return callReverseApi({
    actionName: SAVE_INQUIRIES,
    url: endpoints.INQUIRIES.SPECIALIZED.FINALIZE.compose({
      params: { inquiryType, inquiryId },
    }),
    method: API_METHODS.POST,
    data: (state: IState) => addInquiryProcessPropsToPayload(state.inquiryProcess || {}, apiData),
  });
};
export const createPartialSpecializedInquiryAction = (apiData: any, inquiryType: string) =>
  callReverseApi({
    actionName: SAVE_INQUIRIES,
    url: endpoints.INQUIRIES.SPECIALIZED.PARTIAL.compose({ params: { inquiryType } }),
    method: API_METHODS.POST,
    data: {
      data: {
        attributes: apiData,
      },
    },
  });

export const updateSpecializedInquiryAction = (inquiryId: string, apiData: any) =>
  callReverseApi({
    actionName: 'UPDATE_INQUIRY',
    url: endpoints.INQUIRIES.SPECIALIZED.DETAILS.compose({ params: { inquiryId } }),
    method: API_METHODS.PUT,
    data: {
      data: {
        attributes: apiData,
      },
    },
  });

const updateInquiryTypeSpecificAction = (inquiryType: string, inquiryId: string, apiData: any) =>
  callReverseApi({
    actionName: 'UPDATE_INQUIRY',
    url: endpoints.INQUIRIES.SPECIALIZED.UPDATE.compose({ params: { inquiryType, inquiryId } }),
    method: API_METHODS.PUT,
    data: {
      data: {
        attributes: apiData,
      },
    },
  });

type IInquirySendFunction = (data?: any) => IMakeCallResponse;

const useSendNewSpecializedInquiry = (): IInquirySendFunction => {
  const {
    details: { id: partnerName },
  } = usePartnerConfig();
  const isLead = useSelector(isLeadProcessLane);
  const isLoggedIn = isLoggedInUser();
  const t = useTranslations();
  const { makeCall } = useDispatchApiCall(dispatchOptions(t('errors.submitInquiry')));
  const draftId = useSelector(getInquiryDraftIdSelector);
  const { selectedInquiryType } = useFormConfig();
  const inquiryId = useSelector(getCreatedInquiryId);
  const isCustomer = userIsCustomer();
  const formData = useInquiryValues(partnerName);
  const customerId = useSelector(inquiryDetailsCustomerIdSelector);

  const filterDataByBusinessConditions = useFilterDataByBusinessConditions();

  return async () => {
    let inquiryData;
    switch (selectedInquiryType) {
      case InquiryType.onlinefactoring:
        inquiryData = mapOnlinefactoringToApi(
          { ...formData, customerId, [INQUIRY_DRAFT_ID]: draftId },
          isLoggedIn,
          isLead,
        );
        break;
      case InquiryType.bfs:
        inquiryData = mapBFSInquiryToApi(
          { ...formData, customerId, [INQUIRY_DRAFT_ID]: draftId },
          isCustomer,
        );
        break;
      case InquiryType.profiRLL:
        inquiryData = mapProfiInquiryToApi({ ...formData, customerId }, isCustomer);
        break;
      case InquiryType.bfsService:
        inquiryData = mapBFSServiceInquiryToApi(
          { ...formData, customerId, [INQUIRY_DRAFT_ID]: draftId },
          isLoggedIn,
        );
        break;
      default:
        inquiryData = typeSpecificMapping[selectedInquiryType]({
          ...formData,
          customerId,
          [INQUIRY_DRAFT_ID]: draftId,
        });
        break;
    }

    const dataForApi = pipe(filterEmptyData, filterDataByBusinessConditions)(inquiryData);
    const isOnlineFactoring = selectedInquiryType === InquiryType.onlinefactoring;
    const isLeasePlan = selectedInquiryType === InquiryType.leaseplan;
    const isProfiInquiry = selectedInquiryType === InquiryType.profiRLL;
    const isBFSService = selectedInquiryType === InquiryType.bfsService;

    const apiData = isProfiInquiry ? { ...dataForApi?.data?.attributes } : dataForApi;

    const sendSpecialized = () => createSpecializedInquiryAction(apiData, selectedInquiryType);
    const sendSpecialFinalize = () =>
      createFinalizeSpecializedInquiryAction(dataForApi, selectedInquiryType, inquiryId);

    const sendInquiry =
      isOnlineFactoring || isLeasePlan || isBFSService ? sendSpecialFinalize : sendSpecialized;

    return makeCall(sendInquiry());
  };
};

export const useSendPartialSpecializedInquiry = (): IInquirySendFunction => {
  const {
    details: { id: partnerName },
  } = usePartnerConfig();
  const isLead = useSelector(isLeadProcessLane);
  const isLoggedIn = isLoggedInUser();
  const t = useTranslations();
  const { makeCall } = useDispatchApiCall(dispatchOptions(t('errors.submitInquiry')));
  const { selectedInquiryType } = useFormConfig();

  const filterDataByBusinessConditions = useFilterDataByBusinessConditions();
  const formData = useInquiryValues(partnerName);

  return async () => {
    let inquiryData;
    if (selectedInquiryType === InquiryType.onlinefactoring) {
      inquiryData = mapOnlinefactoringToApi(formData, isLoggedIn, isLead);
    } else {
      inquiryData = typeSpecificMapping[selectedInquiryType](formData);
    }

    const dataForApi = pipe(filterEmptyData, filterDataByBusinessConditions)(inquiryData);

    return makeCall(createPartialSpecializedInquiryAction(dataForApi, selectedInquiryType));
  };
};

const useSendUpdatedSpecializedInquiry = (): IInquirySendFunction => {
  const {
    details: { id: partnerName },
  } = usePartnerConfig();
  const t = useTranslations();
  const { makeCall } = useDispatchApiCall(dispatchOptions(t('errors.submitInquiry')));
  const draftId = useSelector(getInquiryDraftIdSelector);
  const inquiryDetails = useSelector(getInquiryDetailsSelector);
  const inquiryId = useSelector(getInquiryIdSelector) ?? '';
  const isCustomer = userIsCustomer();
  const { selectedInquiryType } = useFormConfig();
  const inquiryValues = useInquiryValues(partnerName);
  const customerId = useSelector(inquiryDetailsCustomerIdSelector);

  const staticData = {
    [INQUIRY_DRAFT_ID]: draftId,
    [COMPEON_PARTNER]: partnerName,
    customerId,
  };

  return (modifiedData?: any) => {
    const isLeasePlan = selectedInquiryType === InquiryType.leaseplan;
    const isBFS = selectedInquiryType === InquiryType.bfs;
    const isProfiInquiry = selectedInquiryType === InquiryType.profiRLL;
    const formData =
      isLeasePlan || isBFS || isProfiInquiry
        ? inquiryValues
        : inquiryDetails && flattenObject(inquiryDetails);

    let inquiryData;
    if (selectedInquiryType === InquiryType.profiRLL) {
      inquiryData = mapProfiInquiryToApi(
        _merge(formData, staticData, modifiedData),
        isCustomer,
        true,
      );
    } else {
      inquiryData = typeSpecificMapping[selectedInquiryType](
        _merge(formData, staticData, modifiedData),
      );
    }

    const dataForApi = pipe(filterEmptyData)(inquiryData);
    const data = isProfiInquiry
      ? { 'form-type': selectedInquiryType, ...dataForApi?.data?.attributes }
      : dataForApi;

    const sendSpecialized = () => updateSpecializedInquiryAction(inquiryId, data);
    const sendSpecialFinalize = () =>
      createFinalizeSpecializedInquiryAction({ ...dataForApi }, selectedInquiryType, inquiryId);
    const updateByInquiryType = () =>
      updateInquiryTypeSpecificAction(selectedInquiryType, inquiryId, dataForApi);

    let sendInquiry = sendSpecialized;
    switch (selectedInquiryType) {
      case InquiryType.leaseplan:
      case InquiryType.bfsService:
        sendInquiry = sendSpecialFinalize;
        break;
      case InquiryType.bfs:
        sendInquiry = updateByInquiryType;
        break;
    }

    return makeCall(sendInquiry());
  };
};

const useSendSpecializedInquiry = (): IInquirySendFunction => {
  const inquiryId = useSelector(getInquiryIdSelector);
  const sendNewInquiry = useSendNewSpecializedInquiry();
  const updateExistingInquiry = useSendUpdatedSpecializedInquiry();

  return inquiryId ? updateExistingInquiry : sendNewInquiry;
};

export default useSendSpecializedInquiry;
